/**
 * useWorkStatusData Hook
 * 라이더 근무 현황 데이터 조회 (API 호출, 응답 매핑)
 */
import { useState, useCallback } from 'react';
import { riderWorkStatusApi } from '../api/riderWorkStatusApi';
import {
    RiderWorkStatusFilters,
    RiderWorkStatusResponseDto,
    SupplierWorkStatus,
    SupplierWorkStatusDto,
    RiderWorkStatusItemDto,
} from '../types/riderWorkStatus.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { removeDateHyphens } from '@/shared/utils/dateUtils';

export const useWorkStatusData = (isSupplierUser: boolean, userId: string | null) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [workStatusData, setWorkStatusData] = useState<SupplierWorkStatus[]>([]);

    /**
     * 라이더 근무 현황 조회
     */
    const fetchWorkStatus = useCallback(
        async (filters: RiderWorkStatusFilters) => {
            try {
                setLoading(true);

                // 날짜 형식 변환 (YYYY-MM-DD -> YYYYMMDD)
                const fromDate = removeDateHyphens(filters.fromDate);
                const toDate = removeDateHyphens(filters.toDate);

                if (!fromDate || !toDate) {
                    showToast('시작일과 종료일을 입력해주세요.', { type: ToastType.WARNING });
                    return;
                }

                // SUPPLIER 사용자는 자동으로 suppId 설정
                let suppId = filters.suppId;
                if (isSupplierUser && userId) {
                    suppId = userId;
                }

                const response = await riderWorkStatusApi.getRiderWorkStatus(fromDate, toDate, suppId);

                // 응답 데이터 처리 및 매핑
                let suppliersData: SupplierWorkStatus[] = [];
                if (response && typeof response === 'object') {
                    const responseData = response as RiderWorkStatusResponseDto;
                    let rawSuppliers: SupplierWorkStatusDto[] = [];

                    if (responseData.suppliers && Array.isArray(responseData.suppliers)) {
                        rawSuppliers = responseData.suppliers;
                    } else if ('data' in response) {
                        const dataResponse = response as { data?: RiderWorkStatusResponseDto };
                        if (dataResponse.data?.suppliers && Array.isArray(dataResponse.data.suppliers)) {
                            rawSuppliers = dataResponse.data.suppliers;
                        }
                    }

                    // deliveryAmtSum을 deliveryFeeTotal로 매핑
                    suppliersData = rawSuppliers.map((supplier: SupplierWorkStatusDto) => ({
                        suppId: supplier.suppId,
                        suppName: supplier.suppName,
                        riderCount: supplier.riderCount,
                        riders: supplier.riders.map((rider: RiderWorkStatusItemDto) => ({
                            riderId: rider.riderId,
                            riderName: rider.riderName,
                            totalWorkDays: rider.totalWorkDays,
                            workStatusByDate: rider.workStatusByDate,
                            deliveryFeeTotal: rider.deliveryAmtSum,
                            totalDeliveryCnt: rider.totalDeliveryCnt,
                        })),
                    }));
                }

                setWorkStatusData(suppliersData);
            } catch {
                showToast('라이더 근무 현황을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                setWorkStatusData([]);
            } finally {
                setLoading(false);
            }
        },
        [isSupplierUser, userId, showToast],
    );

    return {
        loading,
        workStatusData,
        fetchWorkStatus,
    };
};
